export type LegalPageId = 'privacy-policy' | 'terms'

export interface LegalPageSection {
  heading: string
  paragraphs: readonly string[]
  items?: readonly string[]
}

export interface LegalPageRecord {
  id: LegalPageId
  path: string
  title: string
  description: string
  updated: string
  sections: readonly LegalPageSection[]
}

export const legalPages: readonly LegalPageRecord[] = [
  {
    id: 'privacy-policy',
    path: '/Privacy-Policy',
    title: 'Privacy Policy',
    description: 'How Pack Print Mail handles information associated with this public website.',
    updated: 'September 4, 2026',
    sections: [
      {
        heading: 'Information You Choose to Provide',
        paragraphs: [
          'This public website does not create customer accounts or collect payment-card information. If you contact Pack Print Mail by phone or email, we receive the information you choose to provide so we can respond to your question or service request.',
        ],
      },
      {
        heading: 'Website and Security Information',
        paragraphs: [
          'Our hosting and security providers may process basic technical information, such as IP address, browser type, requested pages, timestamps, and security events, to deliver and protect the website.',
        ],
      },
      {
        heading: 'Package Tracking',
        paragraphs: [
          'Tracking numbers entered on this website are normalized in your browser. When you continue, your browser opens the official website of the carrier you selected or that the number format identified. Pack Print Mail does not retrieve or store carrier tracking results through this website.',
        ],
      },
      {
        heading: 'Online Printing and External Websites',
        paragraphs: [
          'The online print shop, carrier websites, map services, and other linked websites operate separately from this public marketing site. Information you submit after following a link is handled according to the notices and terms presented on that destination website.',
        ],
      },
      {
        heading: 'How Information Is Used',
        paragraphs: ['Information associated with this website may be used to:'],
        items: [
          'Respond to customer questions and service requests.',
          'Operate, maintain, troubleshoot, and secure the website.',
          'Comply with legal obligations and protect our customers, business, and systems.',
        ],
      },
      {
        heading: 'Retention and Contact',
        paragraphs: [
          'We retain customer communications and related business records only as reasonably needed for the purpose they were received, normal business administration, dispute resolution, and legal obligations.',
          'Questions about this policy may be sent to info@packprintmail.com or directed to Pack Print Mail LLC, 2761 N HWY 77, Suite 110, Waxahachie, TX 75165.',
        ],
      },
      {
        heading: 'Policy Updates',
        paragraphs: [
          'We may update this policy when the website or our practices change. The current version and its effective date will remain available on this page.',
        ],
      },
    ],
  },
  {
    id: 'terms',
    path: '/Terms',
    title: 'Terms of Use',
    description: 'Terms governing use of the Pack Print Mail public website.',
    updated: 'September 4, 2026',
    sections: [
      {
        heading: 'Using This Website',
        paragraphs: [
          'This website provides general information about Pack Print Mail LLC and its local services. By using the website, you agree to use it lawfully and not interfere with its operation, security, or availability.',
        ],
      },
      {
        heading: 'Service Information',
        paragraphs: [
          'Service availability, carrier acceptance, pricing, turnaround times, store hours, and requirements may change. Website information is not a quote or guarantee. Contact the store for current details before relying on information for a time-sensitive shipment, appointment, or project.',
        ],
      },
      {
        heading: 'Shipping and Carrier Services',
        paragraphs: [
          'Carrier services are provided subject to the applicable carrier’s rules, restrictions, rates, and terms. Pack Print Mail does not control carrier networks, transit events, customs processing, or delivery status shown on carrier websites.',
        ],
      },
      {
        heading: 'Package Tracking and External Links',
        paragraphs: [
          'Package tracking links open official carrier websites. Online printing, mapping, digital mailbox, and other external services may have separate terms and privacy practices. A link does not make Pack Print Mail responsible for another website’s content or operation.',
        ],
      },
      {
        heading: 'Online Printing Orders',
        paragraphs: [
          'Products ordered through the online print shop are subject to the specifications, pricing, proofing requirements, production terms, and policies shown during that ordering process. Contact Pack Print Mail if an online order requires local assistance.',
        ],
      },
      {
        heading: 'Website Content',
        paragraphs: [
          'Unless otherwise identified, the text, branding, organization, and original materials on this website are owned by or used with permission by Pack Print Mail LLC. You may use the website for ordinary personal and business-information purposes but may not misrepresent, reproduce, or exploit its content unlawfully.',
        ],
      },
      {
        heading: 'Disclaimer and Applicable Law',
        paragraphs: [
          'The website is provided on an as-available basis. To the extent permitted by law, Pack Print Mail LLC is not responsible for indirect losses resulting from use of this informational website or an external website. These terms are governed by applicable laws of the State of Texas.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'Questions about these terms may be sent to info@packprintmail.com or directed to Pack Print Mail LLC, 2761 N HWY 77, Suite 110, Waxahachie, TX 75165.',
        ],
      },
    ],
  },
]
